import React from "react";
import { X, SlidersHorizontal, ArrowUpDown } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// One reusable filter + sort bar used on every data page.
// Filters collapse into a single compact "Filters" dropdown button.
// filters: [{ key, label, options: [{value,label}], placeholder }]
// sort: { key, label, options: [{value,label}] }  (optional)
export function FilterBar({ filters = [], sort = null, values, onChange, onClear, children, testid = "filter-bar" }) {
  const active = filters
    .filter((f) => values[f.key])
    .map((f) => ({ key: f.key, label: f.label, text: f.options.find((o) => o.value === values[f.key])?.label || values[f.key] }));
  const activeCount = active.length;

  return (
    <div className="mb-6" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-3">
        {filters.length > 0 && (
          <Popover>
            <PopoverTrigger asChild>
              <button
                data-testid="filters-button"
                className="h-10 inline-flex items-center gap-2 rounded-[10px] bg-nb-surface px-3.5 text-[14px] text-nb-ink"
                style={{ border: "1px solid var(--border-control)" }}
              >
                <SlidersHorizontal size={16} strokeWidth={1.5} /> Filters
                {activeCount > 0 && (
                  <span className="text-[11px] font-semibold rounded-md px-1.5 py-0.5 tnum" style={{ background: "var(--brand-navy)", color: "var(--text-inverse)" }}>{activeCount}</span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-[280px] bg-nb-surface p-4" data-testid="filters-panel">
              <div className="space-y-3">
                {filters.map((f) => (
                  <div key={f.key}>
                    <label className="nb-label block mb-1">{f.label}</label>
                    <Select value={values[f.key] || "all"} onValueChange={(v) => onChange(f.key, v)}>
                      <SelectTrigger
                        className="h-10 w-full rounded-[10px] bg-nb-surface text-[14px]"
                        style={{ border: "1px solid var(--border-control)" }}
                        data-testid={`filter-${f.key}`}
                      >
                        <SelectValue placeholder={f.placeholder || f.label} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{f.placeholder || `All ${f.label.toLowerCase()}`}</SelectItem>
                        {f.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
                {activeCount > 0 && (
                  <button
                    onClick={onClear}
                    data-testid="clear-all-filters-panel"
                    className="text-[13px] font-medium pt-1"
                    style={{ color: "var(--text-link)" }}
                  >
                    Clear all filters
                  </button>
                )}
              </div>
            </PopoverContent>
          </Popover>
        )}

        {children}

        {sort && (
          <div className="ml-auto flex items-center gap-1.5">
            <ArrowUpDown size={16} strokeWidth={1.5} className="text-nb-ink2" />
            <Select value={values[sort.key] || sort.options[0]?.value} onValueChange={(v) => onChange(sort.key, v)}>
              <SelectTrigger
                className="h-10 min-w-[170px] rounded-[10px] bg-nb-surface text-[14px]"
                style={{ border: "1px solid var(--border-control)" }}
                data-testid={`sort-${sort.key}`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {sort.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {active.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mt-3" data-testid="active-filters">
          {active.map((a) => (
            <span
              key={a.key}
              className="inline-flex items-center gap-1.5 text-[13px] text-nb-ink rounded-md pl-2.5 pr-1.5 py-1"
              style={{ background: "var(--bg-selected)" }}
            >
              <span className="text-nb-ink2">{a.label}:</span> {a.text}
              <button
                onClick={() => onChange(a.key, "all")}
                data-testid={`remove-filter-${a.key}`}
                className="w-4 h-4 inline-flex items-center justify-center rounded hover:bg-black/5"
                aria-label={`Remove ${a.label} filter`}
              >
                <X size={13} strokeWidth={2} />
              </button>
            </span>
          ))}
          <button
            onClick={onClear}
            data-testid="clear-all-filters"
            className="text-[13px] font-medium"
            style={{ color: "var(--text-link)" }}
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
}
